import { connect } from '@puzzel/widget-api-lib'

const ORIGINS: string[] = ['*', 'http://localhost:3002', 'https://*.puzzel.com']
const CONNECT_TIMEOUT_MS = 5000
const CONNECT_RETRY_BACKOFF_MS: number[] = [500, 1500, 5000]

// Use the app's existing notion of "full" mode:
// - In development: only when URL contains `&full`
// - Otherwise (production, and the 'test' runner): always full mode
// MODE (not import.meta.env.PROD) so unit tests, which run in Vite mode 'test',
// stay in full mode as they did under process.env.NODE_ENV.
export const isFullMode = (): boolean =>
  import.meta.env.MODE !== 'development' ||
  window.location.search.includes('&full')

const waitForDomReady = async (): Promise<void> => {
  // In most cases the widget script runs after DOM is already ready, but PCC recommends
  // waiting for DOMContentLoaded/load before attempting the Widget API handshake.
  if (document.readyState !== 'loading') return

  await new Promise<void>((resolve) => {
    const cleanup = () => {
      window.removeEventListener('DOMContentLoaded', onDomReady)
      window.removeEventListener('load', onLoad)
    }
    const onDomReady = () => {
      cleanup()
      resolve()
    }
    const onLoad = () => {
      cleanup()
      resolve()
    }
    window.addEventListener('DOMContentLoaded', onDomReady, { once: true })
    window.addEventListener('load', onLoad, { once: true })
  })
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
  name: string,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    const timeoutPromise = new Promise<T>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`${name} timed out after ${timeoutMs}ms`))
      }, timeoutMs)
    })
    return await Promise.race([promise, timeoutPromise])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

// oxlint-disable-next-line typescript/no-explicit-any -- no types here
let apiPromise: any = undefined

export const resetPuzzelApi = (): void => {
  apiPromise = undefined
}

type ConnectRetryInfo = {
  attempt: number
  timeoutMs: number
  backoffMs: number
  error: string
}

type ConnectPuzzelApiOptions = {
  onRetry?: (info: ConnectRetryInfo) => void
}

export const connectPuzzelApi = async (
  options: ConnectPuzzelApiOptions = {},
): Promise<unknown> => {
  if (document.readyState === 'loading') {
    await waitForDomReady()
  }

  let lastError: unknown
  for (let attempt = 0; attempt <= CONNECT_RETRY_BACKOFF_MS.length; attempt++) {
    try {
      return await withTimeout(
        connect(ORIGINS),
        CONNECT_TIMEOUT_MS,
        'Puzzel Widget API connect()',
      )
    } catch (error) {
      lastError = error
      if (attempt < CONNECT_RETRY_BACKOFF_MS.length) {
        const retryInfo: ConnectRetryInfo = {
          attempt: attempt + 1,
          timeoutMs: CONNECT_TIMEOUT_MS,
          backoffMs: CONNECT_RETRY_BACKOFF_MS[attempt] ?? 0,
          error: error instanceof Error ? error.message : String(error),
        }
        options.onRetry?.(retryInfo)
        console.warn('[puzzelApi] connect() failed, retrying', {
          attempt: retryInfo.attempt,
          timeoutMs: retryInfo.timeoutMs,
          backoffMs: retryInfo.backoffMs,
          error: retryInfo.error,
        })
        await sleep(CONNECT_RETRY_BACKOFF_MS[attempt] ?? 0)
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError))
}

// oxlint-disable-next-line typescript/no-explicit-any -- no types here
export const getPuzzelApi = (): any => {
  if (!isFullMode()) {
    return Promise.reject(new Error('Puzzel API unavailable outside full mode'))
  }
  if (apiPromise) return apiPromise

  apiPromise = connectPuzzelApi()

  apiPromise.catch(() => {
    apiPromise = undefined
  })
  return apiPromise
}
